/**
 * Added-to-bag toast — bottom-right, instant optimistic open, background sync.
 */
(function () {
  if (window.__straightAtbBound) return;
  window.__straightAtbBound = true;

  const popup = document.querySelector('[data-straight-atb-popup]');
  if (!popup) return;

  const titleEl = popup.querySelector('[data-straight-atb-title]');
  const imageEl = popup.querySelector('[data-straight-atb-image]');
  const nameEl = popup.querySelector('[data-straight-atb-name]');
  const subtitleEl = popup.querySelector('[data-straight-atb-subtitle]');
  const sizeEl = popup.querySelector('[data-straight-atb-size]');
  const metaEl = popup.querySelector('[data-straight-atb-meta]');
  const priceEl = popup.querySelector('[data-straight-atb-price]');
  const qtyEl = popup.querySelector('[data-straight-atb-qty]');
  const qtyDown = popup.querySelector('[data-straight-atb-qty-down]');
  const qtyUp = popup.querySelector('[data-straight-atb-qty-up]');
  const bagBtn = popup.querySelector('[data-straight-atb-view-bag]');
  const checkoutBtn = popup.querySelector('[data-straight-atb-checkout]');

  const moneyFormat = window.__straightMoneyFormat || '${{amount}}';
  const cartSync = window.StraightCartSync || {};
  const runExclusive =
    cartSync.runExclusive || function (task) { return Promise.resolve().then(task); };
  const hasPendingAdd = cartSync.hasPendingAdd || function () { return false; };
  const hasAnyPendingAdd = cartSync.hasAnyPendingAdd || function () { return false; };
  const readDisplayCount = cartSync.readDisplayCount || function () {
    const badge = document.querySelector('[data-straight-cart-count]');
    return badge ? parseInt(badge.textContent, 10) || 0 : 0;
  };
  const setDisplayCount = cartSync.setDisplayCount || function (count) { return count; };
  const adjustDisplayCount = cartSync.adjustDisplayCount || function () { return 0; };
  const adjustLineQty = cartSync.adjustLineQty || function (_vid, delta) {
    return Math.max(0, delta);
  };
  const getLineQty = cartSync.getLineQty || function () {
    return 0;
  };
  const setLineQty = cartSync.setLineQty || function (_vid, qty) {
    return Math.max(0, qty);
  };
  const publishLineQty = cartSync.publishLineQty || function () {};
  const dbg = cartSync.log || function () {};
  const QTY_DEBOUNCE_MS = 80;
  let lastFocus = null;
  let variantId = null;
  // Shopify's line item key for the current variant's cart line. Preferred
  // over the variant id for /cart/change.js — variant-id lookups are exactly
  // the class of call that 400s with "no valid id or line parameter".
  let lineKey = null;
  let currentQty = 1;
  // Cart line qty before the units shown in this toast session. The stepper
  // always opens at 1; cart target = sessionAnchorQty + display qty.
  let sessionAnchorQty = 0;
  // Quantity-change lifecycle: desired value from the latest click, debounce
  // timer, whether a task is queued behind another cart request, and whether
  // our request is currently on the wire. Busy = any of those is active.
  let qtyDesired = null;
  let qtyQueued = false;
  let qtyRequestInFlight = false;
  let qtyDebounceTimer = null;

  function qtyBusy() {
    return !!(qtyDebounceTimer || qtyQueued || qtyRequestInFlight);
  }

  function formatMoney(cents) {
    const value = (cents || 0) / 100;
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, value.toFixed(2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(value).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.toFixed(2).replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(value).toString());
  }

  function readPdpContext(form) {
    const root = form?.closest('.straight-pdp');
    if (!root) return {};

    const selectedSize = root.querySelector('[data-pdp-size-pill].is-selected');
    const selectedColor = root.querySelector('[data-pdp-swatch].is-selected');

    return {
      size: selectedSize?.getAttribute('data-size-value') || '',
      color: selectedColor?.getAttribute('data-color-value') || '',
    };
  }

  function getPdpImage(form) {
    const root = form?.closest('.straight-pdp');
    const img = root?.querySelector('.straight-pdp__gallery-image');
    return img?.currentSrc || img?.src || '';
  }

  function readVariantData(form) {
    const root = form?.closest('.straight-pdp');
    if (!root) return null;
    const sectionId = root.getAttribute('data-section-id');
    const container =
      root.closest('.shopify-section, .straight-pdp-section, [data-straight-pdp-content]') ||
      root.parentElement ||
      root;
    const sel = '[data-pdp-variants-' + sectionId + ']';
    const node =
      root.querySelector(sel) ||
      container.querySelector(sel) ||
      document.querySelector(sel);
    if (!node) return null;
    try {
      const raw = (node.value != null ? node.value : node.textContent) || '';
      return JSON.parse(raw.trim());
    } catch (e) {
      return null;
    }
  }

  // Build an item from PDP DOM so the toast can render before /cart/add.js resolves.
  function buildOptimisticItem(form) {
    if (!form) return null;
    const root = form.closest('.straight-pdp');
    const idInput = form.querySelector('[data-pdp-variant-id]');
    const vid = idInput ? idInput.value : null;

    const data = readVariantData(form);
    let priceCents = null;
    let variantImage = null;
    if (data && Array.isArray(data.variants)) {
      const match = data.variants.find((v) => String(v.id) === String(vid));
      if (match) {
        priceCents = typeof match.price === 'number' ? match.price : null;
        variantImage = match.image || null;
      }
    }

    const titleEl = root?.querySelector('.straight-pdp__title');
    const image = variantImage || getPdpImage(form);

    return {
      variant_id: vid,
      id: vid,
      product_title: titleEl ? titleEl.textContent.trim() : '',
      title: titleEl ? titleEl.textContent.trim() : '',
      final_price: priceCents,
      price: priceCents,
      price_formatted: priceCents == null ? (root?.querySelector('[data-pdp-price]')?.textContent.trim() || '') : null,
      image: image,
      quantity: 1,
    };
  }

  function readPdpSubtitle(form) {
    const root = form?.closest('.straight-pdp');
    const el = root?.querySelector('.straight-pdp__subtitle');
    return el?.textContent?.trim() || '';
  }

  function readPdpFabric(form) {
    const root = form?.closest('.straight-pdp');
    return root?.getAttribute('data-pdp-fabric')?.trim() || '';
  }

  function pdpHasSizeOption(form) {
    const root = form?.closest('.straight-pdp');
    return root?.getAttribute('data-pdp-has-size') === 'true';
  }

  function isFabricOptionName(name) {
    const n = (name || '').toLowerCase();
    return n === 'tecido' || n === 'fabric' || n === 'material' || n === 'matéria';
  }

  function readItemFabric(item, form) {
    const props = item?.properties || {};
    const fromProperty = props._straight_tecido || props[' _straight_tecido'];
    if (fromProperty) return String(fromProperty).trim();
    return readPdpFabric(form);
  }

  function buildMetaLines(item, context, form) {
    const lines = [];
    const fabric = readItemFabric(item, form);
    if (fabric) lines.push('Tecido, ' + fabric);

    if (item?.options_with_values && item.options_with_values.length) {
      item.options_with_values.forEach(function (opt) {
        if (!opt.value || opt.value === 'Default Title') return;
        if (fabric && isFabricOptionName(opt.name)) return;
        lines.push(opt.name + ', ' + opt.value);
      });
      return lines;
    }

    if (pdpHasSizeOption(form) && context.size) lines.push('Size, ' + context.size);

    let color = context.color;
    if (!color && item?.variant_title && item.variant_title !== 'Default Title') {
      const parts = item.variant_title.split(' / ');
      if (parts.length > 1) color = parts[0];
    }
    if (color) lines.push('Colour, ' + color);

    return lines;
  }

  function readItemSubtitle(item, form) {
    const fromProperty =
      item?.properties &&
      (item.properties._straight_subtitle || item.properties[' _straight_subtitle']);
    if (fromProperty) return String(fromProperty).trim();

    const fromDom = readPdpSubtitle(form);
    if (fromDom) return fromDom;

    return '';
  }

  function currentCartCount() {
    return readDisplayCount();
  }

  function updateBagCount(count) {
    dbg('toast', 'updateBagCount -> ' + count);
    if (bagBtn) bagBtn.textContent = 'Your Bag (' + count + ')';
  }

  function setPrice(cents, formatted) {
    if (!priceEl) return;
    if (cents != null) priceEl.textContent = formatMoney(cents);
    else if (formatted) priceEl.textContent = formatted;
  }

  function resolveItemTitle(item, form, keepExisting) {
    const fromDom = form ? buildOptimisticItem(form) : null;
    const existing = keepExisting && nameEl ? nameEl.textContent.trim() : '';
    return (
      item?.product_title ||
      fromDom?.product_title ||
      fromDom?.title ||
      (item?.title && item.title !== 'Default Title' ? item.title : '') ||
      existing ||
      ''
    );
  }

  function populate(item, context, form, useCurrentCount, options) {
    options = options || {};
    const nextVariantId = item?.variant_id || item?.id || variantId;
    if (String(nextVariantId) !== String(variantId)) lineKey = null;
    variantId = nextVariantId;
    if (item?.key) lineKey = item.key;

    if (!options.skipCounts) {
      const nextCount = useCurrentCount
        ? currentCartCount()
        : currentCartCount() + (item?.quantity || 1);
      setDisplayCount(nextCount);
      updateBagCount(nextCount);
    }

    const imageUrl = item?.image || getPdpImage(form);
    if (imageEl && (imageUrl || !options.preserveMissing)) {
      if (imageUrl) {
        imageEl.src = imageUrl;
        imageEl.alt = resolveItemTitle(item, form, true) || '';
        imageEl.hidden = false;
      } else if (!options.preserveMissing) {
        imageEl.removeAttribute('src');
        imageEl.alt = '';
      }
    }

    if (nameEl) {
      const name = resolveItemTitle(item, form, options.preserveMissing);
      if (name) nameEl.textContent = name;
    }

    if (subtitleEl) {
      const subtitle = readItemSubtitle(item, form) || readPdpSubtitle(form);
      if (subtitle) {
        subtitleEl.textContent = subtitle;
        subtitleEl.hidden = false;
      } else if (!options.preserveMissing) {
        subtitleEl.textContent = '';
        subtitleEl.hidden = true;
      }
    }

    let size = '';
    if (pdpHasSizeOption(form)) {
      size = context.size;
      if (!size && item?.variant_title && item.variant_title !== 'Default Title') {
        const parts = item.variant_title.split(' / ');
        size = parts.length > 1 ? parts[parts.length - 1] : item.variant_title;
      }
    }
    if (sizeEl) {
      if (size) {
        sizeEl.textContent = size;
        sizeEl.hidden = false;
      } else {
        sizeEl.textContent = '';
        sizeEl.hidden = true;
      }
    }

    if (metaEl) {
      metaEl.innerHTML = '';
      const metaLines = buildMetaLines(item, context, form);
      metaLines.forEach((line) => {
        const li = document.createElement('li');
        li.textContent = line;
        metaEl.appendChild(li);
      });
      metaEl.hidden = metaLines.length === 0;
    }

    setPrice(item?.final_price ?? item?.price, item?.price_formatted);

    if (!options.skipQty) {
      currentQty = 1;
      if (qtyEl) qtyEl.textContent = '1';
    }
  }

  function syncSessionAnchorFromCartQty(cartQty) {
    const qty = Math.max(0, cartQty || 0);
    sessionAnchorQty = Math.max(0, qty - currentQty);
  }

  // Each add opens the stepper at 1, even when the variant is already in cart.
  function beginAddSession(vid, delta) {
    const key = String(vid);
    if (delta) adjustLineQty(key, delta);
    syncSessionAnchorFromCartQty(getLineQty(key));
    qtyDesired = null;
    setQtyDisplay(1, true);
    publishLineQty(key, getLineQty(key), 'toast');
  }

  function isSameVariant(item) {
    const vid = item?.variant_id || item?.id;
    return (
      popup.classList.contains('is-open') &&
      variantId &&
      vid &&
      String(variantId) === String(vid)
    );
  }

  const CLOSE_MS = 460;

  let enterFinishTimer = null;
  let closeFinishTimer = null;

  function finishEnterAnimation() {
    if (enterFinishTimer) {
      window.clearTimeout(enterFinishTimer);
      enterFinishTimer = null;
    }
    popup.classList.remove('is-entering');
  }

  function clearCloseTimers() {
    if (closeFinishTimer) {
      window.clearTimeout(closeFinishTimer);
      closeFinishTimer = null;
    }
    popup.removeEventListener('animationend', onCloseEnd);
  }

  function finishCloseAnimation() {
    clearCloseTimers();
    if (!popup.classList.contains('is-open')) {
      popup.classList.remove('is-closing');
      popup.hidden = true;
    }
  }

  function onCloseEnd(e) {
    if (e.target !== popup || e.animationName !== 'straight-atb-exit') return;
    finishCloseAnimation();
  }

  function openShell() {
    lastFocus = document.activeElement;
    clearCloseTimers();
    popup.classList.remove('is-closing', 'is-pop');
    finishEnterAnimation();

    popup.hidden = false;
    void popup.offsetHeight;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      popup.classList.add('is-open');
      updateBagCount(currentCartCount());
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        popup.classList.add('is-entering', 'is-open');
        updateBagCount(currentCartCount());
        enterFinishTimer = window.setTimeout(finishEnterAnimation, 720);
      });
    });
  }

  function onPopEnd(e) {
    if (e.target !== popup || e.animationName !== 'straight-atb-pop') return;
    popup.classList.remove('is-pop');
  }

  function popShell() {
    if (!popup.classList.contains('is-open') || popup.classList.contains('is-closing')) return;

    clearCloseTimers();
    popup.classList.remove('is-closing');
    popup.hidden = false;
    updateBagCount(currentCartCount());

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    popup.classList.remove('is-pop');
    void popup.offsetWidth;
    popup.classList.add('is-pop');
    popup.addEventListener('animationend', onPopEnd, { once: true });
  }

  function closeShell() {
    if (popup.hidden || popup.classList.contains('is-closing')) return;

    finishEnterAnimation();
    clearCloseTimers();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    popup.classList.add('is-closing');
    popup.classList.remove('is-open', 'is-entering');

    if (reducedMotion) {
      finishCloseAnimation();
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      return;
    }

    void popup.offsetHeight;
    popup.addEventListener('animationend', onCloseEnd);
    closeFinishTimer = window.setTimeout(finishCloseAnimation, CLOSE_MS);

    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function bumpQtyDisplay() {
    if (!qtyEl) return;
    qtyEl.classList.remove('is-bump');
    void qtyEl.offsetWidth;
    qtyEl.classList.add('is-bump');
    qtyEl.addEventListener(
      'animationend',
      () => qtyEl.classList.remove('is-bump'),
      { once: true }
    );
  }

  function setQtyDisplay(qty, pending) {
    dbg('toast', 'setQtyDisplay ' + currentQty + ' -> ' + qty + (pending ? ' (pending)' : ''));
    currentQty = qty;
    if (qtyEl) {
      qtyEl.textContent = String(qty);
      qtyEl.classList.toggle('is-pending', !!pending);
    }
    bumpQtyDisplay();
  }

  // Reconcile the toast against a real cart payload (from add / change / refresh).
  function reconcileFromCart(cart) {
    if (!cart) return;
    dbg('toast', 'reconcileFromCart item_count=' + (cart.item_count || 0) +
      ' qtyBusy=' + qtyBusy() + ' pendingAdd=' + hasPendingAdd(variantId));
    if (!qtyBusy() && !hasAnyPendingAdd()) setDisplayCount(cart.item_count || 0);
    if (!variantId || qtyBusy()) return;
    // Skip: a newer Add to Bag for this variant is still in flight, so this
    // cart snapshot is stale and would flicker the quantity backwards.
    if (hasPendingAdd(variantId)) return;
    const item = (cart.items || []).find(
      (line) => String(line.variant_id) === String(variantId)
    );
    if (item) {
      if (item.key) lineKey = item.key;
      if (nameEl && item.product_title) {
        nameEl.textContent = item.product_title;
      }
      setLineQty(variantId, item.quantity);
      if (!qtyBusy()) syncSessionAnchorFromCartQty(item.quantity);
      setPrice(item.final_price ?? item.price);
    }
  }

  // Debounce this toast's +/- clicks (rapid clicks collapse into one request
  // for the final value), then send that single request through the shared
  // global cart queue so it can never overlap with an in-flight Add to Bag
  // or a drawer row edit for the same variant. The display updates instantly;
  // the server just catches up in the background.
  function scheduleQtyChange(nextQty) {
    if (!variantId || nextQty < 1) return;
    const key = String(variantId);
    const sentCartQty = sessionAnchorQty + nextQty;
    const delta = sentCartQty - getLineQty(key);
    qtyDesired = nextQty;
    dbg('toast', 'stepper click vid=' + key + ' display=' + nextQty + ' cart=' + sentCartQty + ' (debouncing)');

    setLineQty(key, sentCartQty);
    setQtyDisplay(nextQty, true);
    if (delta) adjustDisplayCount(delta);
    publishLineQty(key, sentCartQty, 'toast');

    if (qtyDebounceTimer) window.clearTimeout(qtyDebounceTimer);
    qtyDebounceTimer = window.setTimeout(() => {
      qtyDebounceTimer = null;
      // A task is already waiting in the queue — it reads qtyDesired when it
      // actually runs, so it will pick up this newer value on its own.
      if (qtyQueued) return;
      qtyQueued = true;

      runExclusive(async () => {
        qtyQueued = false;
        qtyRequestInFlight = true;
        // Snapshot what we're sending; if qtyDesired moves past this while the
        // request is in flight, this response is stale by definition.
        const sentQty = qtyDesired;
        const sentCartQty = sessionAnchorQty + sentQty;

        // /cart/update.js instead of /cart/change.js: change.js identified by
        // variant id is the call that 400s with "no valid id or line
        // parameter", and it also refuses to *reduce* lines whose product has
        // meanwhile sold out. update.js keyed by the line item key (or variant
        // id as fallback) handles both.
        const updates = {};
        updates[lineKey || key] = sentCartQty;
        dbg('toast', 'POST /cart/update.js', updates);
        try {
          const res = await fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: updates }),
          });
          if (!res.ok) {
            let errBody = null;
            try { errBody = await res.json(); } catch (_) { /* ignore */ }
            dbg('toast', 'update.js FAILED status=' + res.status, errBody);
            throw new Error('update failed');
          }

          const cart = await res.json();
          qtyRequestInFlight = false;

          // If the user kept clicking while this request was in flight, a
          // newer update is already debouncing/queued — applying this response
          // would jump the visible number backwards for a moment.
          if (qtyDebounceTimer || qtyQueued || qtyDesired !== sentQty) {
            dbg('toast', 'update.js OK but STALE (desired=' + qtyDesired + ', sent=' + sentQty + ') — skipping UI apply');
            return;
          }

          dbg('toast', 'update.js OK -> item_count=' + (cart.item_count || 0));
          const item = (cart.items || []).find(
            (line) => String(line.variant_id) === key
          );
          if (item) {
            if (item.key) lineKey = item.key;
            setLineQty(key, item.quantity);
            syncSessionAnchorFromCartQty(item.quantity);
            setQtyDisplay(sentQty, false);
            setPrice(item.final_price ?? item.price);
          }
          setDisplayCount(cart.item_count || 0);
          window.dispatchEvent(
            new CustomEvent('straight:cart:change', { detail: { cart, source: 'toast' } })
          );
        } catch (e) {
          qtyRequestInFlight = false;
          // A newer change is coming right behind this one — let it win.
          if (qtyDebounceTimer || qtyQueued) return;
          // The optimistic display may be wrong now — resync from the server.
          dbg('toast', 'qty change failed, resyncing from /cart.js');
          try {
            const res2 = await fetch('/cart.js', {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            });
            const cart2 = await res2.json();
            const line = (cart2.items || []).find(
              (l) => String(l.variant_id) === key
            );
            if (line && line.key) lineKey = line.key;
            if (line) setLineQty(key, line.quantity);
            if (line) syncSessionAnchorFromCartQty(line.quantity);
            else setQtyDisplay(currentQty, false);
            setDisplayCount(cart2.item_count || 0);
            window.dispatchEvent(
              new CustomEvent('straight:cart:change', { detail: { cart: cart2, source: 'toast' } })
            );
          } catch (_) {
            setQtyDisplay(currentQty, false);
          }
        }
      });
    }, QTY_DEBOUNCE_MS);
  }

  function show({ item, form, updateOnly, addQty, trustQuantity } = {}) {
    const context = readPdpContext(form);
    const optimistic = buildOptimisticItem(form);
    if (!optimistic && !item) return;

    const delta = addQty || optimistic?.quantity || 1;
    dbg('toast', 'show updateOnly=' + !!updateOnly + ' trustQuantity=' + trustQuantity +
      ' delta=' + delta + ' sameVariant=' + isSameVariant(optimistic) +
      (item ? ' itemQty=' + item.quantity : ' (optimistic)'));

    if (updateOnly && popup.classList.contains('is-open')) {
      if (item) {
        // `item` here is the real line from the /cart/add.js response, so its
        // quantity is normally authoritative — except when a newer Add to Bag
        // click for the same variant is still in flight (trustQuantity is
        // false), in which case this response's total is already stale and
        // applying it would flicker the count backwards before the newer
        // click's own response corrects it a moment later.
        populate(item, context, form, true, {
          preserveMissing: true,
          skipQty: true,
          skipCounts: true,
        });
        if (item && trustQuantity !== false) {
          setLineQty(item.variant_id || item.id, item.quantity);
          if (!qtyBusy()) syncSessionAnchorFromCartQty(item.quantity);
        }
      }
      return;
    }

    if (isSameVariant(optimistic)) {
      const vid = optimistic.variant_id || optimistic.id;
      beginAddSession(vid, delta);
      adjustDisplayCount(delta);
      populate(optimistic, context, form, true, {
        preserveMissing: true,
        skipQty: true,
        skipCounts: true,
      });
      popShell();
      return;
    }

    const wasOpen = popup.classList.contains('is-open') && !popup.classList.contains('is-closing');
    const vid = optimistic?.variant_id || optimistic?.id;
    populate(item || optimistic, context, form, !!item, { skipQty: true });
    beginAddSession(vid, delta);
    if (wasOpen) popShell();
    else openShell();
  }

  window.StraightAddedToBag = {
    show,
    close: closeShell,
    reconcile: reconcileFromCart,
    getOptimisticItem: buildOptimisticItem,
  };

  popup.querySelector('[data-straight-atb-close]')?.addEventListener('click', closeShell);

  qtyDown?.addEventListener('click', () => {
    scheduleQtyChange(Math.max(1, (qtyDesired ?? currentQty) - 1));
  });
  qtyUp?.addEventListener('click', () => {
    scheduleQtyChange((qtyDesired ?? currentQty) + 1);
  });

  bagBtn?.addEventListener('click', () => {
    closeShell();
    window.dispatchEvent(new CustomEvent('straight:cart:open', { detail: { skipRefresh: true } }));
  });

  checkoutBtn?.addEventListener('click', () => {
    window.location.href = '/checkout';
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && popup.classList.contains('is-open')) closeShell();
  });

  // Keep the toast in sync when the cart changes elsewhere (add success, drawer edits).
  window.addEventListener('straight:cart:change', (e) => {
    if (!popup.classList.contains('is-open')) return;
    const detail = e.detail || {};
    if (detail.source === 'toast') return;
    reconcileFromCart(detail.cart);
  });

  // Bag button tracks total cart count; title + stepper share line quantity.
  window.addEventListener('straight:cart:count', (e) => {
    if (!popup.classList.contains('is-open')) return;
    updateBagCount(e.detail && e.detail.count != null ? e.detail.count : currentCartCount());
  });

  // Keep toast stepper aligned if the drawer edits the same line while open.
  window.addEventListener('straight:cart:line-qty', (e) => {
    const detail = e.detail || {};
    if (detail.source === 'toast' || !detail.variantId) return;
    if (!popup.classList.contains('is-open')) return;
    if (String(detail.variantId) !== String(variantId)) return;
    syncSessionAnchorFromCartQty(detail.qty);
  });
})();
